import { Mail, ShieldCheck, FileText, Link as LinkIcon, Scale } from 'lucide-react';

export default function terms() {
  const sections = [
    {
      title: 'Overview',
      icon: FileText,
      body: [
        `The njjamaat.org website, referred to as the “Site,” is owned and operated by NJ Jamaat, referred to as the “Owner.” These terms and conditions govern your use of the Site.`,
        `Please read these terms carefully before using the Site. If you do not accept these terms and conditions, you should immediately stop using the Site. By using the Site, you agree to follow these conditions of use.`,
      ],
    },
    {
      title: 'Content Rights',
      icon: ShieldCheck,
      body: [
        `The copyright and all other rights in the material on the Site are owned by the Owner, related entities, or third parties, referred to as “Rights Owners.” Materials on the Site are presented with the permission of the applicable Rights Owners.`,
        `This content may include photographs, images, databases, text, and other audio-visual material. No material on the Site may be reproduced, adapted, altered, republished, distributed, sold, or used commercially without prior written permission from the applicable Rights Owner.`,
        `You may not retrieve content from the Site to create or compile, directly or indirectly, a collection, compilation, database, or directory, whether through automated tools or manual processes, without prior written permission.`,
      ],
    },
    {
      title: 'External Links',
      icon: LinkIcon,
      body: [
        `The Site may contain links to other websites that are not reviewed or controlled by the Owner. The Owner is not responsible for the content, policies, or terms of use of those websites.`,
        `You are responsible for reviewing and following the terms of any external website you access.`,
      ],
    },
    {
      title: 'Privacy',
      icon: ShieldCheck,
      body: [
        `Any privacy policy posted on njjamaat.org forms part of these conditions of use. Questions or requests related to personal data may be sent to pr@njjamaat.org.`,
      ],
    },
    {
      title: 'Use of the Site',
      icon: FileText,
      body: [
        `You may only use the Site in accordance with these terms and for lawful and appropriate purposes.`,
        `The Site is provided on an “as is” basis. There is no guarantee that the Site or its content is error-free, suitable for a particular purpose, accurate, or up to date.`,
        `You are responsible for your use of the Site and for the consequences of relying on any content. You should verify information before relying on it.`,
        `Downloading or using material from the Site is done at your own discretion and risk. To the fullest extent permitted by applicable law, the Owner will not be liable for damage, loss of data, or other harm arising from your use of the Site or linked websites.`,
        `Access to the Site may be suspended, blocked, or terminated at any time without prior notice.`,
      ],
    },
    {
      title: 'Names, Acronyms, Symbols, and Logos',
      icon: ShieldCheck,
      body: [
        `Names, acronyms, symbols, and logos appearing on the Site are owned by their respective Rights Owners and may be protected by law.`,
        `You may not use, adapt, or imply association with them unless you have received prior written permission from the applicable Rights Owner.`,
      ],
    },
    {
      title: 'General Terms',
      icon: FileText,
      body: [
        `Information and material on the Site may be edited, improved, modified, or changed at any time without notice. The Site may also be terminated at any time without notice.`,
        `These terms may be modified from time to time. Any modification is effective immediately upon posting on the Site. Continued access to or use of the Site means you accept the modified terms.`,
        `If you breach these terms, your authorization to access and use the Site will cease. Any material downloaded or printed in violation of these terms must be immediately destroyed.`,
        `If any provision of these terms is found unenforceable by a court of competent jurisdiction, that provision will be enforced to the maximum extent permitted, and the remaining terms will continue in full force and effect.`,
      ],
    },
    {
      title: 'Jurisdiction',
      icon: Scale,
      body: [
        `These terms and conditions are governed by the laws of the United States and, where applicable, the State of New Jersey.`,
        `Any disputes arising from or in connection with these terms shall be handled by courts of competent jurisdiction in the United States.`,
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div
        className="relative py-20 text-white overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #001f3f 0%, #003d66 100%)' }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-64 h-64 rounded-full border-2 border-dawoodi-gold/30"></div>
          <div className="absolute bottom-10 left-20 w-48 h-48 rounded-full border-2 border-dawoodi-gold/30"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-1 h-8 bg-gradient-to-b from-dawoodi-gold to-transparent rounded-full"></div>
            <h1 className="font-playfair text-4xl md:text-5xl font-bold">
              Terms and Conditions
            </h1>
          </div>

          <p className="font-poppins text-white/85 max-w-3xl">
            These terms govern your use of njjamaat.org and related website content.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="mb-10">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-1 h-8 bg-gradient-to-b from-dawoodi-navy to-transparent rounded-full"></div>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-dawoodi-navy">
              Website Terms
            </h2>
          </div>

          <p className="font-poppins text-gray-600 max-w-3xl">
            Please review the following terms before using this website.
          </p>
        </div>

        <div className="space-y-7">
          {sections.map((section, index) => {
            const Icon = section.icon;

            return (
              <section
                key={section.title}
                className={`group relative transition-all duration-300 ${
                  index % 2 === 1 ? 'lg:ml-6' : 'lg:mr-6'
                }`}
              >
                <div className="relative bg-white border-2 border-dawoodi-gold/20 shadow-lg rounded-xl overflow-hidden hover:border-dawoodi-gold hover:shadow-2xl transition-all duration-300">
                  <div className="h-1 bg-gradient-to-r from-dawoodi-navy via-dawoodi-gold to-transparent"></div>

                  <div className="p-5 md:p-7">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-full bg-dawoodi-light/60 border border-dawoodi-gold/30 flex items-center justify-center flex-shrink-0">
                        <Icon size={22} className="text-dawoodi-navy" />
                      </div>

                      <div className="min-w-0">
                        <h3 className="font-playfair font-bold text-xl text-dawoodi-navy">
                          {section.title}
                        </h3>

                        <div className="mt-4 space-y-4">
                          {section.body.map((paragraph, paragraphIndex) => (
                            <p
                              key={paragraphIndex}
                              className="font-poppins text-sm md:text-base text-gray-600 leading-relaxed"
                            >
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            );
          })}
        </div>

        <div className="mt-10 bg-dawoodi-light/40 border-2 border-dawoodi-gold/20 rounded-xl p-5 md:p-7">
          <div className="flex items-start gap-4">
            <Mail size={24} className="text-dawoodi-navy mt-1 flex-shrink-0" />

            <div>
              <h3 className="font-playfair text-xl font-bold text-dawoodi-navy">
                Questions or Permission Requests
              </h3>

              <p className="font-poppins text-gray-600 mt-2">
                For questions about these terms, content permissions, or privacy-related requests,
                contact{' '}
                <a
                  href="mailto:pr@njjamaat.org"
                  className="text-dawoodi-gold hover:underline font-semibold"
                >
                  pr@njjamaat.org
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}